// src/components/Run3D.jsx

import React, { useMemo, useRef, useState, useEffect } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { useLocation } from 'react-router-dom';
import { useThree } from '@react-three/fiber';
import { AppleAidPin } from "./AppleAidPin";
import AdSenseBlock from "../components/AdSenseBlock";
import { AD_SLOTS } from "../constants/adSlots";
import "./Run3D.css";

const Z_SCALE = 35;

// --- 緯度経度からタイル座標を計算 ---
function latLonToTileXY(lat, lon, zoom) {
  const latRad = (lat * Math.PI) / 180;
  const n = 2 ** zoom;
  const x = Math.floor(((lon + 180) / 360) * n);
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  );
  return { x, y };
}

// --- 国土地理院DEMのピクセル値を標高に変換 ---
function decodeGsiDemPixel(r, g, b) {
  const hex = (r << 16) + (g << 8) + b;
  if (hex === 0x800000 || hex === 0 || Number.isNaN(hex)) return null; // 欠損値
  if ((hex & 0x800000) !== 0) {
    return (hex - 0x1000000) * 0.01;
  }
  return hex * 0.01;
}

// --- DEMタイルの読み込み ---
async function fetchDemTile(z, x, y) {
  const urls = [
    `https://cyberjapandata.gsi.go.jp/xyz/dem5a/${z}/${x}/${y}.png`,
    `https://cyberjapandata.gsi.go.jp/xyz/dem_png/${z}/${x}/${y}.png`
  ];

  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const blob = await res.blob();
      const bitmap = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0);
      return ctx.getImageData(0, 0, 256, 256).data;
    } catch (e) {
      // 次のURLへ
    }
  }
  return null;
}

function AidMarkers3D({ aidList, localPoints, parentRef, xyScale }) {
  if (!parentRef?.current) return null;
  if (!aidList?.length || !localPoints?.length) return null;

  return (
    <>
      {aidList.map((aid, i) => {
        const distKm = Number(aid.cumulative_distance ?? aid.distance ?? 0);
        if (Number.isNaN(distKm)) return null;

        const target = localPoints.reduce((prev, curr) =>
          Math.abs(curr.dist - distKm) < Math.abs(prev.dist - distKm)
            ? curr
            : prev
        );

        if (!target) return null;

        const x = target.x / xyScale;
        const y = (target.elevation || 0) / Z_SCALE;
        const z = -target.y / xyScale;

        const worldPos = new THREE.Vector3(x, y + 2, z);
        parentRef.current.localToWorld(worldPos);

        const pinLabel = aid.区分 || `CP${i+1}`;
        
        // ★修正：pinLabelが START または FINISH の場合は下の名前を空にする
        let pinName = aid.区分名称 || aid.name || '';
        if (pinLabel === 'START' || pinLabel === 'FINISH' || pinName.includes('トラックポイント')) {
          pinName = '';
        }

        return (
          <AppleAidPin
            key={i}
            position={[worldPos.x, worldPos.y, worldPos.z]}
            label={pinLabel}
            name={pinName}
          />
        );
      })}
    </>
  );
}

function TerrainMesh({ localPoints, xyScale, terrainSize }) {
  const [demData, setDemData] = useState(null);

  useEffect(() => {
    if (!localPoints.length) return;

    let isMounted = true;
    async function loadDem() {
      let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
      localPoints.forEach(p => {
        if (p.latitude < minLat) minLat = p.latitude;
        if (p.latitude > maxLat) maxLat = p.latitude;
        if (p.longitude < minLon) minLon = p.longitude;
        if (p.longitude > maxLon) maxLon = p.longitude;
      });

      const zoom = 12;
      const tMin = latLonToTileXY(maxLat, minLon, zoom);
      const tMax = latLonToTileXY(minLat, maxLon, zoom);

      const tiles = {};
      for (let x = tMin.x; x <= tMax.x; x++) {
        for (let y = tMin.y; y <= tMax.y; y++) {
          const pixels = await fetchDemTile(zoom, x, y);
          if (pixels) {
            tiles[`${zoom}_${x}_${y}`] = pixels;
          }
        }
      }

      if (isMounted) {
        setDemData({ tiles, zoom });
      }
    }

    loadDem();
    return () => { isMounted = false; };
  }, [localPoints]);

  const geometry = useMemo(() => {
    if (!localPoints.length) return null;

    const GRID = 140;
    const SIZE = terrainSize;
    const geo = new THREE.PlaneGeometry(SIZE, SIZE, GRID, GRID);
    const pos = geo.attributes.position;

    const baseH = localPoints[0].elevation || 0;
    const baseLat = localPoints[0].latitude;
    const baseLon = localPoints[0].longitude;
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 6371000;
    const cosLat = Math.cos(toRad(baseLat));

    for (let i = 0; i < pos.count; i++) {
      const xLocal = pos.getX(i) * xyScale;
      const zLocal = -pos.getZ(i) * xyScale;

      const dLon = xLocal / (R * cosLat);
      const dLat = zLocal / R;
      const lat = baseLat + dLat * (180 / Math.PI);
      const lon = baseLon + dLon * (180 / Math.PI);

      let elevation = null;

      if (demData && demData.tiles) {
        const { zoom } = demData;
        const tileCoord = latLonToTileXY(lat, lon, zoom);
        const key = `${zoom}_${tileCoord.x}_${tileCoord.y}`;
        const pixels = demData.tiles[key];

        if (pixels) {
          const n = 2 ** zoom;
          const xFraction = ((lon + 180) / 360) * n - tileCoord.x;
          const yFraction = ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) * n - tileCoord.y;

          const px = Math.floor(xFraction * 256);
          const py = Math.floor(yFraction * 256);

          if (px >= 0 && px < 256 && py >= 0 && py < 256) {
            const idx = (py * 256 + px) * 4;
            elevation = decodeGsiDemPixel(pixels[idx], pixels[idx + 1], pixels[idx + 2]);
          }
        }
      }

      if (elevation === null) {
        let minDist = Infinity;
        let nearestE = baseH;
        for (let p of localPoints) {
          const dx = pos.getX(i) - (p.x / xyScale);
          const dz = pos.getZ(i) - (-p.y / xyScale);
          const d = dx * dx + dz * dz;
          if (d < minDist) {
            minDist = d;
            nearestE = p.elevation || baseH;
          }
        }
        const distFromRoute = Math.sqrt(minDist);
        const maxDist = terrainSize * 0.4;
        const weight = Math.max(0, 1 - distFromRoute / maxDist);
        elevation = baseH + (nearestE - baseH) * weight;
      }

      const relH = (elevation - baseH) / Z_SCALE;
      pos.setZ(i, relH);
    }

    pos.needsUpdate = true;
    geo.computeVertexNormals();
    return geo;
  }, [localPoints, xyScale, terrainSize, demData]);

  if (!geometry) return null;

  return (
    <mesh
      geometry={geometry}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -5, 0]}
    >
      <meshStandardMaterial
        color="#3a5a40"
        roughness={0.85}
        metalness={0.1}
        flatShading={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function projectToLocalXY(points) {
  if (!points.length) return [];
  const baseLat = points[0].latitude;
  const baseLon = points[0].longitude;
  const toRad = (d) => (d * Math.PI) / 180;
  const cosLat = Math.cos(toRad(baseLat));
  const R = 6371000;

  return points.map((p) => {
    const dx = (p.longitude - baseLon) * toRad(1) * cosLat * R;
    const dy = (p.latitude - baseLat) * toRad(1) * R;
    return {
      ...p,
      x: dx,
      y: dy,
    };
  });
}

function formatTime(sec) {
  const s = Math.floor(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n) => String(n).padStart(2, '0');
  return `${h}:${pad(m)}`;
}

function buildDistanceTable(points) {
  if (!points.length) return [];
  const dist = [0];
  for (let i = 1; i < points.length; i++) {
    const d = haversineDistance(
      points[i - 1].latitude,
      points[i - 1].longitude,
      points[i].latitude,
      points[i].longitude
    );
    dist.push(dist[i - 1] + d);
  }
  return dist;
}

function getLocalPositionAtDistance(localPoints, distTable, targetDist) {
  if (!localPoints.length) return null;
  const total = distTable[distTable.length - 1];

  if (targetDist <= 0) return localPoints[0];
  if (targetDist >= total) return localPoints[localPoints.length - 1];

  let i = 0;
  while (i < distTable.length - 1 && distTable[i + 1] < targetDist) {
    i++;
  }

  const d0 = distTable[i];
  const d1 = distTable[i + 1];
  const t = (targetDist - d0) / (d1 - d0 || 1);
  const p0 = localPoints[i];
  const p1 = localPoints[i + 1];

  return {
    x: p0.x + (p1.x - p0.x) * t,
    y: p0.y + (p1.y - p0.y) * t,
    elevation:
      p0.elevation != null && p1.elevation != null
        ? p0.elevation + (p1.elevation - p0.elevation) * t
        : null,
  };
}

function Runner({ localPoints, distTable, progressRef, currentAltRef, xyScale }) {
  const meshRef = useRef();

  useFrame(() => {
    if (!localPoints.length || !distTable.length) return;
    const total = distTable[distTable.length - 1];
    const targetDist = total * progressRef.current;
    const pos = getLocalPositionAtDistance(localPoints, distTable, targetDist);
    if (!pos || !meshRef.current) return;

    meshRef.current.position.set(
      pos.x / xyScale,
      (pos.elevation || 0) / Z_SCALE,
      -pos.y / xyScale
    );

    const next = getLocalPositionAtDistance(localPoints, distTable, targetDist + 1);
    if (next) {
      meshRef.current.lookAt(
        next.x / xyScale,
        (next.elevation || 0) / Z_SCALE,
        -next.y / xyScale
      );
    }

    currentAltRef.current = pos.elevation || 0;
  });

  return (
    <group ref={meshRef} scale={1.2}>
      <mesh position={[0, 1.6, 0]}>
        <sphereGeometry args={[0.4, 16, 16]} />
        <meshStandardMaterial color="#ff66b2" />
      </mesh>
      <mesh position={[0, 0.8, 0]} rotation={[0.3, 0, 0]}>
        <cylinderGeometry args={[0.25, 0.25, 1.2, 12]} />
        <meshStandardMaterial color="#ff66b2" />
      </mesh>
      <mesh position={[0.35, 1.2, 0.2]} rotation={[0, 0, -0.8]}>
        <cylinderGeometry args={[0.12, 0.12, 0.8, 12]} />
        <meshStandardMaterial color="#ff66b2" />
      </mesh>
      <mesh position={[-0.35, 1.2, -0.2]} rotation={[0, 0, 0.8]}>
        <cylinderGeometry args={[0.12, 0.12, 0.8, 12]} />
        <meshStandardMaterial color="#ff66b2" />
      </mesh>
    </group>
  );
}

function RouteLine({ localPoints, xyScale }) {
  const tube = useMemo(() => {
    if (!localPoints.length) return null;
    const baseH = localPoints[0].elevation || 0;

    const path = new THREE.CatmullRomCurve3(
      localPoints.map(
        (p) =>
          new THREE.Vector3(
            p.x / xyScale,
            (p.elevation || 0) / Z_SCALE,
            -p.y / xyScale
          )
      )
    );

    const radius = 0.15;
    const tubularSegments = localPoints.length * 2;
    const geometry = new THREE.TubeGeometry(path, tubularSegments, radius, 8, false);

    const colors = [];
    const pos = geometry.attributes.position;
    const relHeights = localPoints.map((p) => (p.elevation || 0) - baseH);
    const maxRel = Math.max(...relHeights.map((h) => Math.abs(h)), 1);

    for (let i = 0; i < pos.count; i++) {
      const absH = pos.getY(i) * Z_SCALE;
      const relH = absH - baseH;
      const t = Math.min(Math.max((relH + maxRel) / (2 * maxRel), 0), 1);

      const r = t < 0.5 ? t * 2 : 1.0;
      const g = t < 0.5 ? 1.0 : 1.0 - (t - 0.5) * 2;
      const b = 0.1;

      colors.push(r, g, b);
    }

    geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    return geometry;
  }, [localPoints, xyScale]);

  if (!tube) return null;

  return (
    <mesh geometry={tube}>
      <meshStandardMaterial vertexColors roughness={0.3} metalness={0.2} />
    </mesh>
  );
}

function CameraFollower({ localPoints, distTable, progressRef, xyScale }) {
  const { camera } = useThree();

  useFrame(() => {
    if (!localPoints.length || !distTable.length) return;
    const total = distTable[distTable.length - 1];
    const targetDist = total * progressRef.current;
    const pos = getLocalPositionAtDistance(localPoints, distTable, targetDist);
    if (!pos) return;

    const runnerX = pos.x / xyScale;
    const runnerY = (pos.elevation || 0) / Z_SCALE;
    const runnerZ = -pos.y / xyScale;

    const offset = Math.max(25, Math.min(70, 25 + Math.sqrt(total / 1000) * 9));
    const idealX = runnerX - offset * 0.7;
    const idealY = runnerY + offset * 0.5;
    const idealZ = runnerZ - offset * 0.7;

    camera.position.lerp(new THREE.Vector3(idealX, idealY, idealZ), 0.08);
    camera.lookAt(runnerX, runnerY, runnerZ);
  });

  return null;
}

function Scene({ localPoints, distTable, progressRef, currentAltRef, aidList, parentRef, xyScale, terrainSize }) {
  return (
    <group ref={parentRef}>
      <TerrainMesh localPoints={localPoints} xyScale={xyScale} terrainSize={terrainSize} />
      <RouteLine localPoints={localPoints} xyScale={xyScale} />
      <AidMarkers3D
        aidList={aidList}
        localPoints={localPoints}
        parentRef={parentRef}
        xyScale={xyScale}
      />
      <Runner
        localPoints={localPoints}
        distTable={distTable}
        progressRef={progressRef}
        currentAltRef={currentAltRef}
        xyScale={xyScale}
      />
      <CameraFollower
        localPoints={localPoints}
        distTable={distTable}
        progressRef={progressRef}
        xyScale={xyScale}
      />

      <ambientLight intensity={1.3} />
      <directionalLight position={[200, 400, 200]} intensity={2.0} />
    </group>
  );
}

const hudItemStyle = {
  background: 'rgba(255, 255, 255, 0.35)',
  padding: '8px 14px',
  borderRadius: 12,
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
  fontSize: 13,
  color: '#111',
  backdropFilter: 'blur(14px)',
  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
};

function Run3D() {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const location = useLocation();
  const gpxPoints = location.state?.gpxPoints;
  const aidList = location.state?.aidList ?? [];
  const raceName = location.state?.raceName ?? "レース名";

  const [elapsed, setElapsed] = useState(0);
  const [progress, setProgress] = useState(0);
  const [hudOpacity, setHudOpacity] = useState(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [pausedElapsed, setPausedElapsed] = useState(0);
  
  // エイド表示用ステート
  const [activeAid, setActiveAid] = useState(null);
  const lastPausedAidIndexRef = useRef(-1);

  const progressRef = useRef(0);
  const currentAltRef = useRef(0);
  const startTimeRef = useRef(null);
  const parentRef = useRef();

  const cleanPoints = useMemo(
    () =>
      (gpxPoints || [])
        .filter(
          (p) =>
            p &&
            p.latitude != null &&
            p.longitude != null &&
            !Number.isNaN(Number(p.latitude)) &&
            !Number.isNaN(Number(p.longitude))
        )
        .map((p) => ({
          latitude: Number(p.latitude),
          longitude: Number(p.longitude),
          elevation:
            p.elevation != null && !Number.isNaN(Number(p.elevation))
              ? Number(p.elevation)
              : null,
        })),
    [gpxPoints]
  );

  const distTable = useMemo(() => buildDistanceTable(cleanPoints), [cleanPoints]);
  const totalDistanceM = distTable.length ? distTable[distTable.length - 1] : 0;
  const totalDistanceKm = totalDistanceM / 1000;

  // エイドリストに累積距離（km）を付与
  const formattedAidList = useMemo(() => {
    return (aidList || []).map((aid, index) => {
      const distKm = Number(aid.cumulative_distance ?? aid.distance ?? 0);
      return { ...aid, distKm, originalIndex: index };
    }).filter(aid => !Number.isNaN(aid.distKm));
  }, [aidList]);

  const xyScale = useMemo(() => {
    if (totalDistanceKm <= 0) return 30;
    return Math.max(30, Math.min(90, 20 + Math.sqrt(totalDistanceKm) * 5));
  }, [totalDistanceKm]);

  const terrainSize = useMemo(() => {
    if (totalDistanceKm <= 0) return 1000;
    return Math.max(2000, Math.min(6000, 1500 + totalDistanceKm * 80));
  }, [totalDistanceKm]);

  const localPoints = useMemo(() => {
    const projected = projectToLocalXY(cleanPoints);
    return projected.map((p, i) => ({
      ...p,
      dist: distTable[i] / 1000,
    }));
  }, [cleanPoints, distTable]);

  const togglePlay = () => {
    if (activeAid) {
      setActiveAid(null);
      setIsPlaying(true);
      startTimeRef.current = performance.now() - pausedElapsed * 1000;
      return;
    }

    if (isPlaying) {
      setPausedElapsed(elapsed);
    } else {
      if (progressRef.current >= 1) {
        progressRef.current = 0;
        setProgress(0);
        setElapsed(0);
        setPausedElapsed(0);
        startTimeRef.current = performance.now();
        lastPausedAidIndexRef.current = -1;
      } else {
        startTimeRef.current = performance.now() - pausedElapsed * 1000;
      }
    }
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    if (!distTable.length) return;

    let frameId;
    const speedKmPerHour = 4000;
    const speedMPerSec = (speedKmPerHour * 1000) / 3600;

    const loop = (timestamp) => {
      if (!isPlaying) {
        frameId = requestAnimationFrame(loop);
        return;
      }

      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const dt = (timestamp - startTimeRef.current) / 1000;
      setElapsed(dt);

      const dist = Math.min(dt * speedMPerSec, totalDistanceM);
      const prog = totalDistanceM > 0 ? dist / totalDistanceM : 0;
      const currentDistKm = (totalDistanceM * prog) / 1000;

      // エイド到達チェック（50m以内）＆「関門時間（gateTime）」がある場合のみ一時停止
      if (formattedAidList.length > 0) {
        const nextAidIndex = formattedAidList.findIndex(
          (aid, idx) => 
            idx > lastPausedAidIndexRef.current && 
            Math.abs(aid.distKm - currentDistKm) < 0.05
        );

        if (nextAidIndex !== -1) {
          const targetAid = formattedAidList[nextAidIndex];
          lastPausedAidIndexRef.current = nextAidIndex;
          
          // 関門時間（gateTime）が設定されているエイドのときだけ一時停止する
          const hasGateTime = targetAid.gateTime && targetAid.gateTime.trim() !== "";
          
          if (hasGateTime) {
            progressRef.current = targetAid.distKm / totalDistanceKm;
            setProgress(progressRef.current);
            setPausedElapsed(dt);

            setActiveAid(targetAid);
            setIsPlaying(false);
            return;
          }
        }
      }

      if (prog >= 1) {
        progressRef.current = 1;
        setProgress(1);
        setIsPlaying(false);
        setPausedElapsed(dt);
        return;
      }

      progressRef.current = prog;
      setProgress(prog);

      frameId = requestAnimationFrame(loop);
    };

    frameId = requestAnimationFrame(loop);
    return () => frameId && cancelAnimationFrame(frameId);
  }, [distTable, totalDistanceM, isPlaying, formattedAidList]);

  useEffect(() => {
    let rafId;
    const duration = 600;
    const start = performance.now();

    const fade = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setHudOpacity(t);
      if (t < 1) rafId = requestAnimationFrame(fade);
    };

    rafId = requestAnimationFrame(fade);
    return () => rafId && cancelAnimationFrame(rafId);
  }, []);

  const distanceKm = totalDistanceKm * progress;
  const alt = currentAltRef.current;
  const simTotalTimeSec = location.state?.simTotalTimeSec ?? 0;
  const simTime = progress * simTotalTimeSec;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ width: "100%", height: "700px", position: "relative" }}>
        <Canvas camera={{ position: [0, 200, 300], fov: 45, near: 0.5, far: 20000 }}>
          <Scene
            localPoints={localPoints}
            distTable={distTable}
            progressRef={progressRef}
            currentAltRef={currentAltRef}
            aidList={aidList}
            parentRef={parentRef}
            xyScale={xyScale}
            terrainSize={terrainSize}
          />
        </Canvas>

        {/* HUD */}
        <div
          className="hud"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            opacity: hudOpacity,
            transition: 'opacity 0.6s ease',
            pointerEvents: 'none',
          }}
        >
          <div style={hudItemStyle}>距離 {distanceKm.toFixed(1)} km</div>
          <div style={hudItemStyle}>標高 {Math.round(alt)} m</div>
        </div>

        {/* --- 関門到達時のコンパクトなポップアップ --- */}
        {activeAid && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, calc(-50% + 90px))',
              background: 'rgba(255, 255, 255, 0.90)',
              backdropFilter: 'blur(20px)',
              // ★修正：パディングを調整し、minWidthを小さく（または外す）
              padding: '12px 18px',
              borderRadius: 12,
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
              textAlign: 'center',
              zIndex: 10,
              width: 'max-content', // 中身の長さに合わせる
              maxWidth: '260px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
            }}
          >
            {/* 区分（A4など）と区分名称（黒姫など）をシンプルに横並び、またはコンパクトに配置 */}
            {/* 区分（START/FINISHやA4など）と区分名称の配置 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '2px' }}>
              {activeAid.区分 && (
                <span style={{ fontSize: '11px', fontWeight: 700, background: '#007aff', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>
                  {activeAid.区分}
                </span>
              )}
              {activeAid.区分 !== 'FINISH' && activeAid.区分 !== 'START' && (
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#111' }}>
                  {activeAid.区分名称 || activeAid.name || 'エイドポイント'}
                </span>
              )}
            </div>

            {/* 距離情報 */}
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
              {activeAid.distKm.toFixed(1)} km地点
            </div>

            {/* 関門時間がある場合のみ到着予想を表示 */}
            {activeAid.gateTime && activeAid.gateTime !== "" && (
              <div style={{ fontSize: '13px', color: '#333', fontWeight: 600, marginBottom: '4px' }}>
                到着時間: {activeAid.到着時刻 || '-'}
              </div>
            )}

            {/* 関門時間（gateTime）の表示 */}
            {activeAid.gateTime && activeAid.gateTime !== "" && (
              <div style={{ fontSize: '13px', color: '#e74c3c', fontWeight: 600, marginBottom: '12px' }}>
                関門時間: {activeAid.gateTime}
              </div>
            )}

            {/* FINISH以外の場合のみ「再開」ボタンを表示 */}
            {activeAid.区分 !== 'FINISH' && (
              <button
                onClick={togglePlay}
                style={{
                  padding: '8px 20px',
                  borderRadius: 8,
                  background: '#2ecc71',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  boxShadow: '0 3px 10px rgba(46, 204, 113, 0.3)',
                }}
              >
                ▶ 再開
              </button>
            )}
          </div>
        )}

        {/* 再生 / 停止ボタン */}
        <button
          className="play-btn"
          onClick={togglePlay}
          style={{
            padding: '10px 16px',
            borderRadius: 12,
            background: '#2ecc71', // ポップアップと同じ緑色
            color: '#fff',
            border: 'none',
            boxShadow: '0 4px 12px rgba(46, 204, 113, 0.3)',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {isPlaying ? "⏸ 静止" : "▶ 再生"}
        </button>

        {/* 標高グラデーション凡例 */}
        <div
          className="legend"
          style={{
            padding: '12px 14px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.35)',
            backdropFilter: 'blur(14px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
            fontSize: 12,
            color: '#111',
          }}
        >
          <div style={{ marginBottom: 6 }}>スタートからの相対標高</div>
          <div
            style={{
              height: 10,
              borderRadius: 6,
              background: 'linear-gradient(to right, #2ecc71, #f1c40f, #e67e22, #e74c3c)',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            <span>-（低い）</span>
            <span>+（高い）</span>
          </div>
        </div>

        {/* タイトル */}
        <div
          className="title"
          style={{
            padding: '8px 14px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.35)',
            backdropFilter: 'blur(14px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
            fontSize: 16,
            fontWeight: 600,
            color: '#111',
            pointerEvents: 'none',
          }}
        >
          {raceName}
        </div>
      </div>

      {/* 広告 */}
      <div style={{ marginTop: 20, textAlign: "center" }}>
        <AdSenseBlock adSlot={AD_SLOTS.BOTTOM_3D} />
      </div>
    </div>
  );
}

export default Run3D;